import * as React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

import { AutoRunIcon } from '../../icons/Icons';
import {
  BigQueryLogo,
  DatabricksLogo,
  DbtLogo,
  RedshiftLogo,
  SnowflakeLogo
} from '../../icons/IntegrationLogos';
import { ReactComponent as CustomDockerIlly } from '../../images/illustrations/illustration-custom-docker.svg';
import { ReactComponent as InfDataScaleIlly } from '../../images/illustrations/illustration-infinite-data-scale.svg';
import { ReactComponent as ZeroHassleIlly } from '../../images/illustrations/illustration-zero-hassle.svg';
import { ANIMATIONS, DOCS_LINKS, INTERNAL_LINKS } from '../../utils';
import { Button, Heading } from '../design-system';
import { GridItem, type GridItemType } from '../global/GridItem';
import { InfoText } from '../global/InfoText';
import { Integ } from '../global/IntegrationsTout';

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: column;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  width: 100%;
  padding: 0 40px;
  margin-bottom: 150px;
  position: relative;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding-top: 60px;
    margin-bottom: 130px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    margin-bottom: 80px;
  }
`;

const Top = styled.div`
  max-width: 480px;
  position: relative;
  margin-bottom: 390px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    padding: 0;
    margin-bottom: 30px;
  }
`;

const AndSoMuchMore = styled.div`
  margin: 0 auto;
  width: 100%;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    display: flex;
    flex-direction: column;
  }
`;

const Illustration = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 160px;
  flex: none;
  overflow: hidden;
  transition: transform ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};
`;

const Info = styled.div`
  padding: 22px 20px 10px;
  transition: transform ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};
`;

const StyledGridItem = styled(GridItem)`
  &&& {
    padding: 5px;

    &:hover {
      ${Illustration} {
        transform: scale(1.04) translateY(-5px);
      }

      ${Info} {
        transform: translateY(-3px);
      }
    }
  }
`;

const SteppedDataViz = styled.div`
  position: absolute;
  left: -394px;
  top: -173px;
  width: 1802px;
  height: 1000px;
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    display: none;
  }
`;

const ConnectToAnything = styled.div`
  position: absolute;
  z-index: 1;
  top: 265px;
  right: 381px;
  display: flex;
  flex-direction: column;
`;

const ConnectToAnythingLogos = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  column-gap: 7px;
`;

const ConnectToAnythingButton = styled(Button)`
  height: 24px;
  opacity: 0.7;
`;

const DAGIlly = styled.img`
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

export const ReactiveExecutionTout: React.ComponentType = React.memo(() => {
  const NEW_WAYS: GridItemType[] = [
    {
      title: 'Infinite data scale',
      illustration: <InfDataScaleIlly />,
      link: DOCS_LINKS.QueryMode,
      description:
        'Work with all of your data, and push compute down to the warehouse without worrying about memory limits.'
    },
    {
      title: 'Zero hassle',
      illustration: <ZeroHassleIlly />,
      link: DOCS_LINKS.EnvironmentViews,
      description:
        'Hex is fully hosted, with securely-sharable connections, customized environments, and configurable compute.'
    },
    {
      title: 'Custom Docker images',
      illustration: <CustomDockerIlly />,
      link: DOCS_LINKS.CustomImages,
      description:
        'BYO from ECR or DockerHub, for consistent, reproducible environments.'
    }
  ];

  return (
    <Wrapper {...ANIMATIONS.SimpleFadeIn}>
      <Top>
        <InfoText
          description="Hex’s graph-based execution model makes projects more reproducible, explainable, and performant than traditional notebooks."
          icon={<AutoRunIcon />}
          link={DOCS_LINKS.ReactiveExecution}
          title="Intelligent execution"
        />
        <SteppedDataViz>
          <ConnectToAnything>
            <ConnectToAnythingLogos>
              <Integ
                iconOnly={true}
                logo={<SnowflakeLogo />}
                slug="snowflake"
              />
              <Integ iconOnly={true} logo={<RedshiftLogo />} slug="redshift" />
              <Integ iconOnly={true} logo={<BigQueryLogo />} slug="bigquery" />
              <Integ
                iconOnly={true}
                logo={<DatabricksLogo />}
                slug="databricks"
              />
              <Integ iconOnly={true} logo={<DbtLogo />} slug="dbt" />
            </ConnectToAnythingLogos>
            <ConnectToAnythingButton
              link={INTERNAL_LINKS.Integrations}
              size="small"
              stylez="minimal"
              text="And more..."
            />
          </ConnectToAnything>
          <DAGIlly
            alt="Reactive DAG"
            src="/images/notebooks/reactive-dag.png"
            srcSet={`/images/notebooks/reactive-dag.png 1x, /images/notebooks/reactive-dag@2x.png 2x`}
          />
        </SteppedDataViz>
      </Top>
      <AndSoMuchMore>
        <Grid>
          {NEW_WAYS.map((item, index) => (
            <StyledGridItem
              key={index}
              isExternal={true}
              showBkgGradient={item.showBkgGradient}
              to={item.link}>
              <>
                <Illustration>{item.illustration}</Illustration>
                <Info>
                  <Heading
                    css={`
                      margin-bottom: 6px;
                    `}
                    renderAs="h4">
                    {item.title}
                  </Heading>
                  <div>{item.description}</div>
                </Info>
              </>
            </StyledGridItem>
          ))}
        </Grid>
      </AndSoMuchMore>
    </Wrapper>
  );
});
